import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { INDUSTRIES } from "../lib/content";

const DETAIL = {
  "Software & SaaS": "ARR, deferred revenue, ASC 606, and unit economics done right.",
  "Fintech": "Regulatory-ready ledgers, reconciliations, and treasury operations.",
  "Healthtech": "Complex revenue cycles, HIPAA-adjacent controls, grant accounting.",
  "E-commerce": "Multi-channel COGS, inventory, and gross-margin visibility.",
  "Consumer Brands": "Wholesale, DTC, and marketplace-agnostic reporting.",
  "Marketplaces": "Take-rate models, GMV vs revenue, escrow accounting.",
  "Climate & Deep Tech": "Grant tracking, R&D credits, and long-horizon planning.",
  "Professional Services": "Utilization, WIP, and project-level profitability.",
};

const Overline = ({ children }) => (
  <span className="inline-block font-sans text-[11px] md:text-xs uppercase tracking-[0.28em] text-[#C5A880] font-semibold">
    {children}
  </span>
);

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Overline>Industries We Serve</Overline>
            <h1 className="mt-8 font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium text-[#0A192F] leading-[0.98]">
              Accounting and advisory expertise <br /> <span className="italic">across industries.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="font-sans text-slate-600 leading-relaxed max-w-md">
              We partner with startups and growing businesses across a wide range of industries,
              providing CPA-led accounting, financial reporting, and advisory services tailored to
              each client&apos;s unique operations, goals, and stage of growth.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
        <ul className="divide-y divide-[#E2E8F0] border-t border-b border-[#E2E8F0]">
          {INDUSTRIES.map((ind, i) => (
            <li key={ind} data-testid={`industry-${i}`} className="group py-8 md:py-10 grid grid-cols-12 gap-4 items-center">
              <span className="col-span-2 md:col-span-1 font-sans text-xs text-[#C5A880] tracking-[0.25em]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="col-span-10 md:col-span-4 font-serif text-3xl md:text-4xl text-[#0A192F] tracking-tight">
                {ind}
              </h3>
              <p className="col-span-12 md:col-span-6 font-sans text-slate-600 leading-relaxed">
                {DETAIL[ind]}
              </p>
              <div className="hidden md:flex md:col-span-1 justify-end">
                <ArrowUpRight
                  size={22}
                  strokeWidth={1.25}
                  className="text-slate-300 group-hover:text-[#C5A880] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                />
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-[#0A192F] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Overline>Your Industry Not Listed?</Overline>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              We tailor our services <br /> to <span className="italic text-[#C5A880]">your business.</span>
            </h2>
            <p className="mt-6 font-sans text-slate-300 leading-relaxed max-w-xl">
              Every business is different. Our CPA-led team provides accounting and advisory
              solutions customized to your industry, operations, and goals.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="industries-cta-btn"
              className="inline-flex items-center gap-3 bg-[#C5A880] text-[#0A192F] px-8 py-4 font-sans text-[12px] tracking-[0.22em] uppercase hover:bg-white transition-all hover:-translate-y-0.5"
            >
              Contact Our Team <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
